/**
 * Shared appointment create/edit modal used by the provider calendar, the
 * appointment page, and the in-session sidebar (compact chrome).
 */

import {
  Box,
  Button,
  Chip,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconBan, IconReceipt, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import type { Appointment, RelatedPerson } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { getVideoMeetingUrl } from '@unify/fhir-model/resources/appointment';
import { APPOINTMENT_MODALITY_OPTIONS } from '@/pages/appointments/utils/appointmentModality';
import { appointmentToCalendarEvent } from './appointmentToCalendarEvent';
import type { OverlayPortalProps } from './AppointmentSharedFields';
import {
  buildSessionAppointmentArgs,
  saveScheduledAppointmentWrite,
} from './sessionAppointmentArgs';
import {
  SessionAppointmentFormFields,
  type SessionAppointmentFieldsValue,
} from './SessionAppointmentFormFields';
import {
  resolveDefaultSessionTypeId,
  withRelatedPersonAttendeeOptions,
} from './sessionSchedulerOptions';
import { useSessionSchedulerOptions } from './useSessionSchedulerOptions';
import { listGroupFacilitatorOptions } from '@/lib/groupTherapy';
import { AppointmentServiceCodesTable } from '@/components/AppointmentServiceCodesTable';
import { AppointmentParticipantsTable } from '@/components/AppointmentParticipantsTable';
import { SectionHeader } from '@/components/SectionHeader';
import { AppointmentEditorHeader } from './AppointmentEditorHeader';
import { appointmentEditorTitle, type AppointmentEditorSource } from './appointmentEditorNav';
import {
  actorRefIsAppointmentParticipant,
  shouldShowCalendarJoin,
} from './calendarEventGuards';
import {
  buildEditableAppointmentParticipantRows,
  previewServiceCodeRowsForSessionType,
  withParticipantRowModality,
} from './appointmentEditorTables';
import { findOrCreateAdHocAppointmentRelatedPerson } from './adHocAppointmentRelatedPerson';
import {
  useAppointmentEditorTables,
  type UseAppointmentEditorTablesResult,
} from './useAppointmentEditorTables';
import type { ProviderCalendarEventCategory } from './types';
import type { AdHocParticipantInput } from '@/components/ParticipantCardsPanel';

function timeLabel(date: Date): string {
  const hour = date.getHours();
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(date.getMinutes()).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function emptyValues(): SessionAppointmentFieldsValue {
  return {
    episodeOfCareId: null,
    client: null,
    provider: '',
    sessionType: '',
    sessionAttendees: [],
    attendanceStatus: 'scheduled',
    date: null,
    startTime: '9:00 AM',
    endTime: '10:00 AM',
    recurringEnabled: false,
    recurringInterval: 1,
    recurringTimeframe: 'week',
    recurringDaysOfWeek: [],
    recurringEndType: 'occurrences',
    recurringEndDate: null,
    recurringOccurrences: 10,
    modality: 'telehealth',
    siteId: 'telehealth',
    telehealthFromOffice: false,
    officeForTelehealth: '',
    roomId: null,
    deviceIds: [],
  };
}

export type { AppointmentEditorSource };

export interface AppointmentEditorModalProps {
  opened: boolean;
  onClose: () => void;
  source: AppointmentEditorSource;
  appointment?: Appointment;
  organizationId: string;
  viewerPractitionerId: string;
  episodeOfCareId?: string;
  patientId?: string;
  patientDisplay?: string;
  groupSession?: boolean;
  defaultStart?: Date;
  onSaved: (appointment: Appointment) => void;
  overlay?: OverlayPortalProps & {
    modalZIndex?: number;
    modalPortalTarget?: HTMLElement | null;
  };
  title?: string;
  episodeDisplayName?: string;
  groupDisplayName?: string;
  tables?: UseAppointmentEditorTablesResult;
  readOnly?: boolean;
  videoLink?: string;
  onJoinVideo?: () => void;
  showJoinVideo?: boolean;
  onDelete?: () => void;
  /** Clinician cancel — persists cancelled status + declined client participants. */
  onCancelAppointment?: () => void;
  calendarCancelling?: boolean;
  /** Callers that know the event exists before `appointment` finishes loading. */
  editing?: boolean;
  category?: ProviderCalendarEventCategory;
  onCategoryChange?: (category: ProviderCalendarEventCategory) => void;
  participantRefs?: string[];
  participantOptions?: { value: string; label: string }[];
  onParticipantRefsChange?: (participantRefs: string[]) => void;
  children?: ReactNode;
  onCalendarSave?: () => void;
  calendarSaveDisabled?: boolean;
  calendarSaving?: boolean;
}

function sessionSidebarTitle(editing: boolean, creating: boolean): string {
  if (creating) return 'Schedule next appointment';
  if (editing) return 'Reschedule appointment';
  return 'Appointment';
}

export function AppointmentEditorModal({
  opened,
  onClose,
  source,
  appointment,
  organizationId,
  viewerPractitionerId,
  episodeOfCareId,
  patientId,
  patientDisplay,
  groupSession = false,
  defaultStart,
  onSaved,
  overlay,
  title: titleProp,
  episodeDisplayName,
  groupDisplayName,
  tables: tablesProp,
  readOnly = false,
  videoLink: videoLinkProp,
  onJoinVideo,
  showJoinVideo: showJoinVideoProp,
  onDelete,
  onCancelAppointment,
  calendarCancelling = false,
  editing: editingProp,
  category: categoryProp,
  onCategoryChange,
  participantRefs: participantRefsProp,
  participantOptions: participantOptionsProp,
  onParticipantRefsChange,
  children,
  onCalendarSave,
  calendarSaveDisabled,
  calendarSaving,
}: AppointmentEditorModalProps): JSX.Element {
  const medplum = useMedplum();
  const editing = editingProp ?? Boolean(appointment?.id);
  const isCalendar = source === 'calendar';
  const isSidebar = source === 'session-sidebar';
  const category: ProviderCalendarEventCategory =
    categoryProp
    ?? (groupSession ? 'group' : 'session');
  const showSessionForm = !isCalendar;
  const showParticipantTable =
    source !== 'session-sidebar'
    && (category === 'session' || (editing && category === 'group'));
  const showServiceCodes =
    editing
    && (category === 'session' || category === 'group')
    && source !== 'session-sidebar';

  const [values, setValues] = useState<SessionAppointmentFieldsValue>(emptyValues);
  const [description, setDescription] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coFacilitatorIds, setCoFacilitatorIds] = useState<string[]>([]);
  const [coFacilitatorOptions, setCoFacilitatorOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (!opened || !groupSession || !showSessionForm) return;
    let cancelled = false;
    void listGroupFacilitatorOptions(medplum)
      .then((opts) => !cancelled && setCoFacilitatorOptions(opts))
      .catch(() => !cancelled && setCoFacilitatorOptions([]));
    return () => {
      cancelled = true;
    };
  }, [opened, groupSession, medplum, showSessionForm]);

  const existingEvent = useMemo(
    () => (appointment ? appointmentToCalendarEvent(appointment) : null),
    [appointment],
  );

  const coFacilitatorSelectData = useMemo(() => {
    const byId = new Map(coFacilitatorOptions.map((o) => [o.value, o.label]));
    for (const p of existingEvent?.additionalParticipants ?? []) {
      if (p.resourceType === 'Practitioner' && !byId.has(p.id)) {
        byId.set(p.id, p.display ?? p.id);
      }
    }
    return [...byId]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [coFacilitatorOptions, existingEvent]);

  useEffect(() => {
    if (!opened || !showSessionForm) return;
    setError(null);
    if (existingEvent) {
      setValues({
        ...emptyValues(),
        episodeOfCareId: existingEvent.episodeOfCareId ?? episodeOfCareId ?? null,
        client: existingEvent.client ?? patientDisplay ?? null,
        provider: existingEvent.practitionerId ?? viewerPractitionerId,
        sessionType: existingEvent.sessionTypeId ?? '',
        sessionAttendees: groupSession
          ? []
          : [
            ...(existingEvent.patientId ? [`Patient/${existingEvent.patientId}`] : []),
            ...(existingEvent.additionalParticipants ?? [])
              .filter((p) => p.resourceType === 'RelatedPerson')
              .map((p) => `RelatedPerson/${p.id}`),
          ],
        attendanceStatus:
          existingEvent.status && existingEvent.status !== 'entered-in-error'
            ? existingEvent.status
            : 'scheduled',
        date: existingEvent.start,
        startTime: timeLabel(existingEvent.start),
        endTime: timeLabel(existingEvent.end),
        modality: existingEvent.modality ?? 'telehealth',
        siteId:
          existingEvent.modality === 'telehealth' ? 'telehealth' : (existingEvent.siteId ?? ''),
        telehealthFromOffice:
          existingEvent.modality === 'telehealth' && Boolean(existingEvent.siteId),
        officeForTelehealth:
          existingEvent.modality === 'telehealth' ? (existingEvent.siteId ?? '') : '',
        roomId: existingEvent.roomId ?? null,
        deviceIds: existingEvent.deviceIds ?? [],
      });
      setDescription(existingEvent.description ?? '');
      setVideoLink(existingEvent.videoLink ?? '');
      const primaryProviderId = existingEvent.practitionerId ?? viewerPractitionerId;
      setCoFacilitatorIds(
        groupSession
          ? (existingEvent.additionalParticipants ?? [])
            .filter((p) => p.resourceType === 'Practitioner' && p.id !== primaryProviderId)
            .map((p) => p.id)
          : [],
      );
      return;
    }
    const start = defaultStart ?? null;
    setCoFacilitatorIds([]);
    setValues({
      ...emptyValues(),
      episodeOfCareId: episodeOfCareId ?? null,
      client: patientDisplay ?? null,
      provider: viewerPractitionerId,
      sessionAttendees: patientId ? [`Patient/${patientId}`] : [],
      date: start,
      ...(start ? { startTime: timeLabel(start) } : {}),
    });
    setDescription('');
    setVideoLink('');
  }, [
    opened,
    showSessionForm,
    existingEvent,
    episodeOfCareId,
    patientId,
    patientDisplay,
    groupSession,
    viewerPractitionerId,
    defaultStart,
  ]);

  const options = useSessionSchedulerOptions({
    enabled: opened && showSessionForm,
    organizationId,
    viewerPractitionerId,
    values,
    existingAppointmentId: appointment?.id,
    savedAttendanceStatus: existingEvent?.status,
    editing,
    selectedSessionTypeLabel: existingEvent?.sessionTypeDisplay,
  });

  useEffect(() => {
    if (!opened || !showSessionForm || editing) return;
    setValues((prev) => {
      const preferred = resolveDefaultSessionTypeId({
        schedulableTypes: options.episodeFilteredTypes,
        currentSessionTypeId: prev.sessionType,
        standardSessionTypeId: options.standardSessionTypeId,
      });
      if (!preferred) return prev;
      return { ...prev, sessionType: preferred };
    });
  }, [
    opened,
    showSessionForm,
    editing,
    options.episodeFilteredTypes,
    options.standardSessionTypeId,
  ]);

  const patch = (next: Partial<SessionAppointmentFieldsValue>): void =>
    setValues((prev) => ({ ...prev, ...next }));

  const internalTables = useAppointmentEditorTables({
    appointment: (showParticipantTable || showServiceCodes) && !tablesProp ? appointment : undefined,
    enabled: opened && (showParticipantTable || showServiceCodes) && !tablesProp,
    selectedHealthcareServiceId: values.sessionType || undefined,
  });
  const selectedSessionType = useMemo(
    () => options.allSessionTypes.find((type) => type.id === values.sessionType),
    [options.allSessionTypes, values.sessionType],
  );
  const tables = useMemo(() => {
    const base = tablesProp ?? internalTables;
    if (!selectedSessionType || base.hasChargeItems) return base;
    const preview = previewServiceCodeRowsForSessionType(selectedSessionType);
    if (preview.length === 0) return base;
    return { ...base, serviceCodeRows: preview };
  }, [internalTables, selectedSessionType, tablesProp]);

  const formModality = isCalendar ? undefined : values.modality;
  const selectedParticipantRefs = useMemo(
    () => (isCalendar ? (participantRefsProp ?? []) : values.sessionAttendees),
    [isCalendar, participantRefsProp, values.sessionAttendees],
  );
  const subjectPatientId =
    patientId
    ?? options.selectedEpisode?.patientId
    ?? existingEvent?.patientId;

  const [clientRelatedPersons, setClientRelatedPersons] = useState<RelatedPerson[]>([]);
  useEffect(() => {
    if (!opened || !showParticipantTable || category !== 'session' || !subjectPatientId) {
      setClientRelatedPersons([]);
      return;
    }
    let cancelled = false;
    void medplum
      .searchResources('RelatedPerson', { patient: `Patient/${subjectPatientId}`, _count: '100' })
      .then((found) => !cancelled && setClientRelatedPersons(found))
      .catch(() => !cancelled && setClientRelatedPersons([]));
    return () => {
      cancelled = true;
    };
  }, [opened, showParticipantTable, category, subjectPatientId, medplum]);

  const participantOptions = useMemo(
    () =>
      withRelatedPersonAttendeeOptions(
        isCalendar ? (participantOptionsProp ?? []) : options.attendeeOptions,
        clientRelatedPersons,
      ),
    [isCalendar, participantOptionsProp, options.attendeeOptions, clientRelatedPersons],
  );
  const relatedPersonsByIdForRows = useMemo(() => {
    const map: Record<string, RelatedPerson | undefined> = {
      ...(tables.relatedPersonsById ?? {}),
    };
    for (const related of clientRelatedPersons) {
      if (related.id) map[related.id] = related;
    }
    return map;
  }, [clientRelatedPersons, tables.relatedPersonsById]);
  const participantRowsForModal = useMemo(() => {
    const existingRows = withParticipantRowModality(tables.participantRows, formModality);
    if (category !== 'session') return existingRows;
    return buildEditableAppointmentParticipantRows({
      selectedRefs: selectedParticipantRefs,
      options: participantOptions,
      existingRows,
      subjectPatientId,
      modality: formModality,
      relatedPersonsById: relatedPersonsByIdForRows,
      patientsById: tables.patientsById,
    });
  }, [
    category,
    formModality,
    participantOptions,
    relatedPersonsByIdForRows,
    selectedParticipantRefs,
    subjectPatientId,
    tables.participantRows,
    tables.patientsById,
  ]);
  const availableParticipantOptions = useMemo(() => {
    const selected = new Set(selectedParticipantRefs);
    return participantOptions.filter((option) => !selected.has(option.value));
  }, [participantOptions, selectedParticipantRefs]);
  const setParticipantRefs = (next: string[]): void => {
    if (isCalendar) {
      onParticipantRefsChange?.(next);
      return;
    }
    patch({ sessionAttendees: next });
  };

  const handleAddAdHocParticipant = async (input: AdHocParticipantInput): Promise<void> => {
    if (!subjectPatientId) {
      throw new Error('Select a client before adding participants');
    }
    const episodeId =
      values.episodeOfCareId
      ?? existingEvent?.episodeOfCareId
      ?? episodeOfCareId
      ?? options.selectedEpisode?.id
      ?? undefined;
    const related = await findOrCreateAdHocAppointmentRelatedPerson(medplum, {
      patientId: subjectPatientId,
      episodeId,
      name: input.name,
      email: input.email,
      phone: input.phone,
    });
    if (!related.id) {
      throw new Error('Could not create related person');
    }
    const ref = `RelatedPerson/${related.id}`;
    setClientRelatedPersons((prev) =>
      prev.some((person) => person.id === related.id) ? prev : [...prev, related],
    );
    if (!selectedParticipantRefs.includes(ref)) {
      setParticipantRefs([...selectedParticipantRefs, ref]);
    }
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    if (!values.date) {
      setError('Pick a date for the appointment.');
      return;
    }
    if (!values.provider) {
      setError('Select a provider.');
      return;
    }
    if (!values.sessionType) {
      setError('Select a session type.');
      return;
    }

    const write = buildSessionAppointmentArgs(
      { ...values, recurring: 'none', description, videoLink },
      {
        viewerPractitionerId,
        viewerPreferredDisplay: options.viewerPreferredDisplay,
        organizationId,
        patientId: options.selectedEpisode?.patientId ?? patientId,
        patientDisplay: options.selectedEpisode?.clientName ?? patientDisplay,
        providerPreferredDisplay: options.providerPreferredDisplay,
        sessionTypes: options.allSessionTypes,
        attendeeLabelByRef: options.attendeeLabelByRef,
        hasExistingScheduledResources: Boolean(
          existingEvent?.roomId || (existingEvent?.deviceIds?.length ?? 0) > 0,
        ),
        groupSession,
        coFacilitators: groupSession
          ? coFacilitatorIds.map((id) => ({
              id,
              preferredDisplay: options.practitionerPreferredDisplayById.get(id),
            }))
          : undefined,
      },
    );
    if (!write) {
      setError('End time must be after the start time.');
      return;
    }

    setSaving(true);
    try {
      const saved = await saveScheduledAppointmentWrite(medplum, write, {
        appointmentId: appointment?.id,
        viewerPractitionerId,
      });
      notifications.show({
        color: 'green',
        message: editing
          ? source === 'appointment-page'
            ? 'Appointment updated'
            : 'Appointment rescheduled'
          : 'Next appointment scheduled',
      });
      onSaved(saved);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save the appointment';
      setError(message);
      notifications.show({
        color: 'red',
        title: editing ? 'Could not reschedule' : 'Could not schedule',
        message,
      });
    } finally {
      setSaving(false);
    }
  };

  const resolvedVideoLink =
    videoLinkProp
    || videoLink
    || (appointment ? getVideoMeetingUrl(appointment) : undefined)
    || existingEvent?.videoLink;
  const resolvedModality = isCalendar
    ? undefined
    : values.modality ?? existingEvent?.modality;
  const showJoinVideo = showJoinVideoProp ?? shouldShowCalendarJoin({
    isTelehealthOrHybrid: resolvedModality === 'telehealth' || resolvedModality === 'hybrid',
    isParticipant: actorRefIsAppointmentParticipant(
      appointment?.participant,
      viewerPractitionerId ? `Practitioner/${viewerPractitionerId}` : undefined,
    ),
    hasVideoMeeting: Boolean(resolvedVideoLink?.trim()),
  });

  const headerTitle = isSidebar
    ? sessionSidebarTitle(editing, !editing)
    : appointmentEditorTitle({
        category,
        groupDisplayName,
        episodeDisplayName,
        sessionTypeDisplay: existingEvent?.sessionTypeDisplay,
        existingTitle: existingEvent?.title ?? titleProp,
        clientDisplayName: patientDisplay ?? existingEvent?.client,
        editing,
        fallback: titleProp,
      });

  const useRichHeader = !isSidebar;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={!useRichHeader}
      title={
        useRichHeader ? (
          <AppointmentEditorHeader
            source={source}
            title={headerTitle}
            appointmentId={appointment?.id}
            clientDisplayName={patientDisplay ?? existingEvent?.client}
            clientChartHref={
              (patientId ?? existingEvent?.patientId)
                ? `/dashboard/clients/${patientId ?? existingEvent?.patientId}`
                : undefined
            }
            videoLink={resolvedVideoLink}
            showJoinVideo={showJoinVideo}
            showCopyMeetingLink={showJoinVideo}
            onClose={onClose}
            onJoinVideo={onJoinVideo}
          />
        ) : (
          headerTitle
        )
      }
      size="720px"
      zIndex={overlay?.modalZIndex}
      {...(overlay?.modalPortalTarget
        ? { portalProps: { target: overlay.modalPortalTarget } }
        : {})}
      styles={{
        root: { pointerEvents: 'auto' },
        header: { borderBottom: '1px solid var(--mantine-color-gray-5)', paddingBottom: 16 },
        title: useRichHeader ? { width: '100%', flex: 1 } : undefined,
        body: { paddingTop: 20 },
      }}
    >
      <Stack gap="md">
        <Box style={readOnly ? { pointerEvents: 'none', opacity: 0.65 } : undefined}>
          {isCalendar && !editing && onCategoryChange ? (
            <>
              <Chip.Group
                value={category}
                onChange={(v) => {
                  const next = Array.isArray(v) ? v[0] : v;
                  if (next === 'session' || next === 'group' || next === 'meeting' || next === 'time-entry') {
                    onCategoryChange(next);
                  }
                }}
              >
                <Group gap="xs">
                  <Chip value="session" color="gold">
                    Session
                  </Chip>
                  <Chip value="group" color="gold">
                    Group
                  </Chip>
                  <Chip value="meeting" color="gold">
                    Meeting
                  </Chip>
                  <Chip value="time-entry" color="gold">
                    Time Entry
                  </Chip>
                </Group>
              </Chip.Group>
              <Box mt="sm" />
            </>
          ) : null}

          {showSessionForm ? (
            <>
              <SessionAppointmentFormFields
                values={values}
                onChange={patch}
                editing={editing}
                lockEpisode
                hideAttendees={!isSidebar}
                hideClientSelection={groupSession}
                coFacilitators={
                  groupSession
                    ? {
                      options: coFacilitatorSelectData,
                      value: coFacilitatorIds,
                      onChange: setCoFacilitatorIds,
                    }
                    : undefined
                }
                episodeOptions={options.episodeOptions}
                episodesLoading={options.episodesLoading}
                episodeDetails={options.episodeDetails}
                providerOptions={options.providerOptions}
                providersLoading={options.providersLoading}
                sessionTypeOptions={options.sessionTypeOptions}
                attendeeOptions={options.attendeeOptions}
                statusOptions={options.statusOptions}
                sessionTypeDurations={options.sessionTypeDurations}
                sites={options.sites}
                siteOptions={options.siteOptions}
                firstSiteId={options.firstSiteId}
                rooms={options.rooms}
                portableDevices={options.portableDevices}
                fixedInRoom={options.fixedInRoom}
                busyByRoom={options.busyByRoom}
                busyByDevice={options.busyByDevice}
                comboboxProps={overlay?.comboboxProps}
                popoverProps={overlay?.popoverProps}
              />
              <Textarea
                label="Notes"
                placeholder="Optional notes for this appointment"
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                minRows={2}
                autosize
              />
            </>
          ) : (
            children
          )}
        </Box>

        {showParticipantTable || showServiceCodes ? (
          <>
            <Divider />
            <Stack gap="md">
              {showParticipantTable ? (
                <AppointmentParticipantsTable
                  rows={participantRowsForModal}
                  appearance="connectedCards"
                  onAttendanceChange={
                    !readOnly && editing ? tables.onAttendanceChange : undefined
                  }
                  savedStatusByPatientId={tables.savedAttendanceByPatientId}
                  onLocationChange={
                    !readOnly && editing ? tables.onLocationChange : undefined
                  }
                  locationOptionsByPatientId={tables.locationOptionsByPatientId}
                  sessionModality={formModality}
                  onModalityChange={
                    !readOnly && !isCalendar
                      ? (modality) => patch({ modality })
                      : undefined
                  }
                  modalityOptions={APPOINTMENT_MODALITY_OPTIONS}
                  participantOptions={availableParticipantOptions}
                  onAddParticipant={
                    !readOnly && category === 'session'
                      ? (participantRef) =>
                        setParticipantRefs([...selectedParticipantRefs, participantRef])
                      : undefined
                  }
                  onAddAdHocParticipant={
                    !readOnly && category === 'session' && Boolean(subjectPatientId)
                      ? handleAddAdHocParticipant
                      : undefined
                  }
                  onRemoveParticipant={
                    !readOnly && category === 'session'
                      ? (participantRef) =>
                        setParticipantRefs(
                          selectedParticipantRefs.filter((ref) => ref !== participantRef),
                        )
                      : undefined
                  }
                />
              ) : null}
              {showServiceCodes ? (
                <Stack gap="xs">
                  <SectionHeader icon={<IconReceipt size={16} />} label="Service codes" />
                  <AppointmentServiceCodesTable
                    rows={tables.serviceCodeRows}
                    appearance="card"
                  />
                </Stack>
              ) : null}
            </Stack>
          </>
        ) : null}

        {error ? (
          <Text size="sm" c="red">
            {error}
          </Text>
        ) : null}

        <Divider />
        {readOnly ? (
          <Group justify="flex-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </Group>
        ) : isCalendar ? (
          <Group justify="space-between">
            <Group>
              {editing && onDelete ? (
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={onDelete}
                  disabled={calendarSaving || calendarCancelling}
                >
                  Delete Event
                </Button>
              ) : null}
              {editing && onCancelAppointment ? (
                <Button
                  variant="light"
                  color="yellow"
                  leftSection={<IconBan size={16} />}
                  onClick={onCancelAppointment}
                  loading={calendarCancelling}
                  disabled={calendarSaving}
                >
                  Cancel Appointment
                </Button>
              ) : null}
              {!editing ? <Box /> : null}
            </Group>
            <Group>
              {!editing ? (
                <Button variant="outline" onClick={onClose} disabled={calendarSaving}>
                  Cancel
                </Button>
              ) : null}
              <Button
                color="gold"
                loading={calendarSaving}
                onClick={() => onCalendarSave?.()}
                disabled={calendarSaveDisabled || calendarCancelling}
              >
                {editing ? 'Update Event' : 'Create Event'}
              </Button>
            </Group>
          </Group>
        ) : source === 'appointment-page' ? (
          <Group gap="md" wrap="nowrap" style={{ width: '100%' }}>
            <Button variant="default" onClick={onClose} disabled={saving} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button
              color="gold"
              loading={saving}
              onClick={() => void handleSave()}
              style={{ flex: 2 }}
            >
              Save changes
            </Button>
          </Group>
        ) : (
          <Group justify="flex-end">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button color="gold" loading={saving} onClick={() => void handleSave()}>
              {editing ? 'Save changes' : 'Schedule'}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

/** Sidebar and legacy call sites — compact titles, session/group form only. */
export type SessionSchedulerModalProps = Omit<AppointmentEditorModalProps, 'source'> & {
  source?: AppointmentEditorSource;
};

export function SessionSchedulerModal(props: SessionSchedulerModalProps): JSX.Element {
  return <AppointmentEditorModal {...props} source={props.source ?? 'session-sidebar'} />;
}
